import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Form, Input, Button, Select, message, Spin, Alert } from "antd";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import {
  getPublicShowingSchedule,
  bookPublicShowing,
} from "../../lib/api/publicShowingApi";
import { APP_NAME } from "../../helpers/constants";

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f8faf8 0%, #f0f4f1 100%);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08);
  padding: 1.5rem 1.25rem 1.75rem;
  margin-top: 0.5rem;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 600;
  color: #1a2e1b;
  margin: 0 0 0.35rem;
  line-height: 1.3;
`;

const Sub = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: #5c6b5e;
  line-height: 1.45;
`;

const Brand = styled.div`
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #2c5f2d;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const PublicShowingSchedulePage = () => {
  const { slug } = useParams();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["public-showing", slug],
    queryFn: () => getPublicShowingSchedule(slug),
    enabled: !!slug,
  });

  const property = data?.property;
  const slots = data?.slots ?? [];
  const addressLine = [property?.address, property?.city, property?.state]
    .filter(Boolean)
    .join(", ");

  const onFinish = async (values) => {
    if (!values.slotKey) {
      message.error("Choose a time slot.");
      return;
    }
    const [slotStart, slotEnd] = values.slotKey.split("||");
    setSubmitting(true);
    try {
      await bookPublicShowing(slug, {
        name: values.name,
        phone: values.phone,
        email: values.email,
        slotStart,
        slotEnd,
      });
      setDone(true);
      message.success("Request sent.");
    } catch (e) {
      message.error(e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Helmet>
        <title>
          {property?.address
            ? `Book a showing · ${property.address}`
            : `Book a showing · ${APP_NAME}`}
        </title>
      </Helmet>
      <Brand>{APP_NAME}</Brand>
      <Card>
        {isLoading && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
          </div>
        )}
        {isError && (
          <Alert
            type="error"
            message={error?.message || "Unavailable"}
            showIcon
          />
        )}
        {!isLoading && !isError && done && (
          <>
            <Title>Request received</Title>
            <Sub>
              Thanks - the landlord has your showing request. They may reach out
              to confirm.
            </Sub>
          </>
        )}
        {!isLoading && !isError && !done && property && (
          <>
            <Title>Book a showing</Title>
            <Sub>{addressLine || property.address || "Property"}</Sub>
            {slots.length === 0 ? (
              <Alert
                type="info"
                showIcon
                message="No open slots right now"
                description="Check back later or contact the listing directly."
              />
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark={false}
              >
                <Form.Item
                  name="name"
                  label="Full name"
                  rules={[{ required: true, message: "Enter your name" }]}
                >
                  <Input
                    size="large"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Phone"
                  rules={[{ required: true, message: "Enter your phone" }]}
                >
                  <Input
                    size="large"
                    type="tel"
                    placeholder="Mobile number"
                    autoComplete="tel"
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Enter your email" },
                    { type: "email", message: "Invalid email" },
                  ]}
                >
                  <Input
                    size="large"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </Form.Item>
                <Form.Item
                  name="slotKey"
                  label="Preferred time"
                  rules={[{ required: true, message: "Select a slot" }]}
                >
                  <Select
                    size="large"
                    placeholder="Choose a time"
                    options={slots.map((s) => ({
                      value: `${s.start}||${s.end}`,
                      label: s.label,
                    }))}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting}
                    style={{ background: "#2c5f2d", borderColor: "#2c5f2d" }}
                  >
                    Request showing
                  </Button>
                </Form.Item>
              </Form>
            )}
          </>
        )}
      </Card>
    </Page>
  );
};

export default PublicShowingSchedulePage;
